import React, { useEffect, useState, useRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles, shorthands, tokens, Spinner, mergeClasses } from '@fluentui/react-components';

import { Overlay } from '@bingads-webui-react/fabric-control';
import { PlayPauseButton } from './play-pause-button';
import { Scrubber } from './scrubber';
import { VolumeControls } from './volume-controls';
import { TimeAndDuration } from './time-and-duration';

export const PREVIEW_TYPES = {
  MP4: 'MP4',
  CLIPCHAMP: 'CLIPCHAMP',
};

function withPermanentUrl(assetResolver) {
  return async (id) => {
    const [file, asset] = await assetResolver(id);
    file.getTranscoderInputFile();
    return [file.permanentUrl, asset];
  };
}

function getUrlFileExtension(url) {
  if (url) {
    const parsedUrl = new URL(url);
    const { pathname } = parsedUrl;
    const lastDotIndex = pathname.lastIndexOf('.');

    if (lastDotIndex === -1 || lastDotIndex === pathname.length - 1) {
      return '';
    }

    return pathname.substring(lastDotIndex + 1);
  }
  return '';
}

class ErrorBoundary extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.logger = props && props.logger;
    this.i18n = props && props.i18n;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (this.logger) {
      this.logger.logTrace('ClipchampPlayer - Error caught in ErrorBoundary:', error);
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <div>
          <h2>{this.i18n && this.i18n.getString(_TL_('Something went wrong.'))}</h2>
        </div>
      );
    }
    return this.props.children;
  }
}

const useStyles = makeStyles({
  player: {
    cursor: 'pointer',
    width: '100%',
    '&:hover .play-pause-btn': {
      backgroundColor: tokens.colorNeutralForeground1,
      color: tokens.colorNeutralBackground1,
    },
    '&:hover:active .play-pause-btn': {
      backgroundColor: tokens.colorNeutralForeground1,
      color: tokens.colorNeutralBackground1,
    },
  },
  playerContainer: {
    width: '100%',
  },
  playerActions: {
    position: 'relative',
    bottom: '60px',
    left: '20px',
    alignItems: 'center',
    width: 'min-content',
    height: '24px',
    display: 'flex',
    ...shorthands.gap('4px'),
  },
  playerScrubber: {
    width: '100%',
    position: 'relative',
    display: 'flex',
    bottom: '70px',
  },
  spinnerContainer: {
    marginTop: '220px',
    width: '100%',
  },
  spinnerOverlay: {
    marginTop: '150px',
  },
});

export const ClipchampPlayer = ({
  videoUrl,
  width,
  logger,
  loadClipchampPlayer,
  i18n,
  poster,
}) => {
  const [project, setProject] = useState(null);
  const [playerOptions, setPlayerOptions] = useState(null);
  const [clipchamp, setClipchamp] = useState(null);
  const [loadingClipchamp, setLoadingClipchamp] = useState(true);
  const [previewType, setPreviewType] = useState(PREVIEW_TYPES.MP4);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    if (previewType !== PREVIEW_TYPES.CLIPCHAMP) {
      return;
    }
    if (!loadClipchampPlayer) {
      setLoadingClipchamp(false);
      return;
    }

    const loadPlayer = async () => {
      const props = await loadClipchampPlayer();
      setClipchamp(props);
      setLoadingClipchamp(false);
    };

    loadPlayer();
  }, [loadClipchampPlayer, previewType]);

  useEffect(() => {
    if (previewType !== PREVIEW_TYPES.CLIPCHAMP) {
      return;
    }

    const fetchDataAsync = async () => {
      setIsLoading(true);

      const request = new Request(videoUrl);
      setPlayerOptions(null);
      setProject(null);

      fetch(request).then((response) => {
        if (response.status === 200) {
          return response.json();
        }
        throw new Error('Error occurred while loading project file');
      })
        .then((response) => {
          const proj = response;
          const playerOpts = {
            assetResolver: withPermanentUrl(clipchamp.createAssetResolver({
              getAssetById: (id) => {
                const asset = proj.assets.byId[id];
                if (!asset) {
                  throw new Error('Asset not found for id: ', id);
                }
                return Promise.resolve(asset);
              },
              handlers: [clipchamp.UrlAssetFetchHandler, clipchamp.createHubbleAssetFetchHandler('test')],
            })),
          };
          setProject(proj);
          setPlayerOptions(playerOpts);
          setIsLoading(false);
        }).catch((error) => {
          logger && logger.logError('Error loading Clipchamp video', error);
        });
    };
    if (!loadingClipchamp && clipchamp) {
      fetchDataAsync();
    }
  }, [videoUrl, logger, clipchamp, loadingClipchamp, previewType]);

  // determine video type
  useEffect(() => {
    const videoType = getUrlFileExtension(videoUrl);
    if (videoType.toUpperCase() === PREVIEW_TYPES.CLIPCHAMP) {
      setPreviewType(PREVIEW_TYPES.CLIPCHAMP);
    } else {
      setPreviewType(PREVIEW_TYPES.MP4);
    }
  }, [videoUrl, previewType, setPreviewType]);

  const videoRef = useRef();

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoUrl]);


  if (previewType === PREVIEW_TYPES.CLIPCHAMP) {
    if ((!loadClipchampPlayer) || (!loadingClipchamp && !clipchamp)) {
      return <div className="clipchamp-player-load-failure">{i18n && i18n.getString(_TL_('Player cannot be initialized.'))}</div>;
    }


    if (clipchamp && clipchamp.Player) {
      const { Player } = clipchamp;
      return (
        <div className={mergeClasses('clipchamp-video-player', classes.playerContainer)}>
          {(isLoading || loadingClipchamp || _.isNull(playerOptions) || _.isNull(project)) ? (
            <div className={classes.spinnerContainer}>
              <Overlay className={classes.spinnerOverlay}>
                <Spinner className="page-load-spinner" useCustomSize />
              </Overlay>
            </div>
          ) : (
            <ErrorBoundary logger={logger} i18n={i18n}>
              <Player
                options={playerOptions}
                project={project}
                style={{
                  borderRadius: '4px 4px 0px 0px',
                  height: 'auto',
                }}
                preload={10}
              >
                <div className={classes.playerScrubber}>
                  <Scrubber
                    usePlayerTime={clipchamp.usePlayerTime}
                    usePlayerControls={clipchamp.usePlayerControls}
                  />
                </div>
                <div className={classes.playerActions}>
                  <PlayPauseButton
                    usePlayPause={clipchamp.usePlayPause}
                  />
                  <VolumeControls
                    usePlayerControls={clipchamp.usePlayerControls}
                    i18n={i18n}
                  />
                  <TimeAndDuration
                    useFormattedTimeAndDuration={clipchamp.useFormattedTimeAndDuration}
                  />
                </div>
              </Player>
            </ErrorBoundary>
          )}
        </div>
      );
    }
    return null;
  }
  return (
    <div
      className="html-video-player"
      style={{
 display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 'auto',
}}
    >
      <video controls ref={videoRef} width={width} height="auto" poster={poster}>
        <track kind="captions" />
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

ClipchampPlayer.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  width: PropTypes.string.isRequired,
  logger: PropTypes.shape({
    logError: PropTypes.func,
  }).isRequired,
  loadClipchampPlayer: PropTypes.func,
};

ClipchampPlayer.defaultProps = {
  loadClipchampPlayer: _.noop,
};
